import React, { useState, useEffect } from 'react';
import { X, User, Phone, MapPin, Save, AlertTriangle } from 'lucide-react';
import { Member, Town } from '../../types';
import { PhotoUpload } from '../Common/PhotoUpload';
import { darkModeClasses } from '../../utils/darkMode';
import { HelpButton } from '../Help/HelpButton';
import { memberApi } from '../../services/api';
import { useGlobalToast } from '../../contexts/ToastContext';

interface EditMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (member: Member) => void;
  member: Member | null;
  towns: Town[];
}

export const EditMemberModal: React.FC<EditMemberModalProps> = ({
  isOpen,
  onClose,
  onSave,
  member,
  towns
}) => {
  const toast = useGlobalToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    gender: 'Male' as 'Male' | 'Female',
    ageGroup: 'Adult' as 'Youth' | 'Adult' | 'Elder',
    townId: '',
    dateOfBirth: '',
    profilePhoto: null as string | null,
    houseLocation: '',
    homeTown: '',
    emergencyContact: '',
    nhisNumber: '',
    ghanaCardNumber: '',
    email: '',
    status: 'active' as string,
    deceasedDate: '',
    deceasedNotes: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (member) {
      setFormData({
        name: member.name,
        phone: member.phone,
        gender: member.gender,
        ageGroup: member.ageGroup,
        townId: member.townId,
        dateOfBirth: member.dateOfBirth || '',
        profilePhoto: member.profilePhoto || null,
        houseLocation: member.houseLocation || '',
        homeTown: member.homeTown || '',
        emergencyContact: member.emergencyContact || '',
        nhisNumber: member.nhisNumber || '',
        ghanaCardNumber: member.ghanaCardNumber || '',
        email: member.email || '',
        status: member.status || 'active',
        deceasedDate: member.deceasedDate || new Date().toISOString().split('T')[0],
        deceasedNotes: member.deceasedNotes || ''
      });
    }
  }, [member]);


  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^(\+233|0)[2-9]\d{8}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid Ghana phone number';
    }

    if (!formData.townId) {
      newErrors.townId = 'Please select a town';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !member) return;

    // Check if status is changing to deceased
    if (formData.status === 'deceased' && member.status !== 'deceased') {
      try {
        const response = await memberApi.flagDeceased(member.id, formData.deceasedDate, formData.deceasedNotes);
        if (response.success) {
          toast.success('Member flagged as deceased successfully');
          // Continue to update other fields if needed, OR just close. 
          // Since flagDeceased might be separate, we should check if other fields changed.
          // However, to keep it simple and safe, we can proceed to update the rest of the profile as well
          // or just rely on parent onSave to refresh.
          // But wait, the parent onSave expects the member object to update local state.
          // We should probably construct the updated member and pass it to onSave.

          const updatedMember: Member = {
            ...member,
            ...formData,
            // Ensure status is updated in the object passed back
            status: 'deceased',
            deceasedDate: formData.deceasedDate,
            deceasedNotes: formData.deceasedNotes,
            gender: formData.gender as "Male" | "Female",
            ageGroup: formData.ageGroup as "Youth" | "Adult" | "Elder",
            profilePhoto: formData.profilePhoto || undefined
          };

          onSave(updatedMember);
          onClose();
          setErrors({});
          return;
        } else {
          toast.error(response.error || 'Failed to flag member as deceased');
          return;
        }
      } catch (error) {
        console.error('Error flagging deceased:', error);
        toast.error('An error occurred while updating member status');
        return;
      }
    }

    const updatedMember: Member = {
      ...member,
      ...formData,
      gender: formData.gender as "Male" | "Female",
      ageGroup: formData.ageGroup as "Youth" | "Adult" | "Elder",
      profilePhoto: formData.profilePhoto || undefined
    };


    onSave(updatedMember);
    onClose();
    setErrors({});
  };

  if (!isOpen || !member) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-bg-primary rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border-primary">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary flex items-center">
            <User className="h-6 w-6 mr-2 text-blue-600" />
            Edit Member
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <strong>Username:</strong> {member.username} (Cannot be changed)
            </p>
          </div>

          <PhotoUpload
            currentPhoto={formData.profilePhoto}
            onPhotoChange={(photo) => setFormData({ ...formData, profilePhoto: photo })}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Full Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.name ? 'border-red-500' : ''
                }`}
              placeholder="Enter full name"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formData.status === 'deceased' ? 'border-red-300 text-red-600' : ''
                  }`}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="deceased">Deceased</option>
              </select>
            </div>

            {formData.status === 'deceased' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Date of Death
                </label>
                <input
                  type="date"
                  value={formData.deceasedDate}
                  onChange={(e) => setFormData({ ...formData, deceasedDate: e.target.value })}
                  className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent border-red-300`}
                  required
                />
              </div>
            )}
          </div>

          {formData.status === 'deceased' && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 space-y-3">
              <div className="flex items-center text-red-800 dark:text-red-200 mb-2">
                <AlertTriangle className="h-5 w-5 mr-2" />
                <span className="font-semibold">Deceased Member Details</span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Additional Notes
                </label>
                <textarea
                  value={formData.deceasedNotes}
                  onChange={(e) => setFormData({ ...formData, deceasedNotes: e.target.value })}
                  className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent min-h-[80px]`}
                  placeholder="Enter any relevant details..."
                />
              </div>
              <p className="text-xs text-red-600 dark:text-red-300">
                Warning: Marking a member as deceased will stop future assignment generation. Active assignments may need manual review.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Phone Number *
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className={`w-full ${darkModeClasses.input} rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.phone ? 'border-red-500' : ''
                  }`}
                placeholder="+233 24 123 4567"
              />
            </div>
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Gender
              </label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value as "Male" | "Female" })}
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>

            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Age Group
              </label>
              <select
                value={formData.ageGroup}
                onChange={(e) => setFormData({ ...formData, ageGroup: e.target.value as "Youth" | "Adult" | "Elder" })}
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              >
                <option value="Youth">Youth</option>
                <option value="Adult">Adult</option>
                <option value="Elder">Elder</option>
              </select>

            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Date of Birth
            </label>
            <input
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
              className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                House Location
              </label>
              <input
                type="text"
                value={formData.houseLocation}
                onChange={(e) => setFormData({ ...formData, houseLocation: e.target.value })}
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="House number, street name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Home Town
              </label>
              <input
                type="text"
                value={formData.homeTown}
                onChange={(e) => setFormData({ ...formData, homeTown: e.target.value })}
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="Original hometown"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              placeholder="member@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Emergency Contact
            </label>
            <input
              type="tel"
              value={formData.emergencyContact}
              onChange={(e) => setFormData({ ...formData, emergencyContact: e.target.value })}
              className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              placeholder="+233 24 000 0000"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                NHIS Number
              </label>
              <input
                type="text"
                value={formData.nhisNumber}
                onChange={(e) => setFormData({ ...formData, nhisNumber: e.target.value })}
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="NHIS123456789"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Ghana Card Number
              </label>
              <input
                type="text"
                value={formData.ghanaCardNumber}
                onChange={(e) => setFormData({ ...formData, ghanaCardNumber: e.target.value })}
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="GHA-123456789-0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Town Assignment *
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={formData.townId}
                onChange={(e) => setFormData({ ...formData, townId: e.target.value })}
                className={`w-full ${darkModeClasses.input} rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.townId ? 'border-red-500' : ''
                  }`}
              >
                <option value="">Select Town</option>
                {towns.map(town => (
                  <option key={town.id} value={town.id}>{town.name}</option>
                ))}
              </select>
            </div>
            {errors.townId && <p className="text-red-500 text-sm mt-1">{errors.townId}</p>}
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 dark:bg-dark-bg-tertiary hover:bg-gray-200 dark:hover:bg-dark-hover-bg text-gray-800 dark:text-dark-text-primary py-2 px-4 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <Save className="h-4 w-4" />
              <span>Save Changes</span>
            </button>
            <HelpButton
              guideId="members/add-edit-members"
              variant="icon"
              size="md"
            />
          </div>
        </form>
      </div>
    </div>
  );
};